import React from "react";
import {
  Box,
  Card,
  CardHeader,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { styles } from "../styles/publicStyles";
import {
  OVERVIEW_PER_CATEGORY,
  groupByCategory,
  isListed,
  isUnlocked,
} from "../data/resources";
import TruncatedText from "./TruncatedText";

const TypeIcon = ({ resource }) => {
  const theme = useTheme();
  const isLink = resource.type === "link";
  const Icon = isLink ? LinkOutlinedIcon : DescriptionOutlinedIcon;
  return (
    <Tooltip title={isLink ? "Link" : "File"} arrow>
      <Icon
        aria-label={isLink ? "Link" : "File"}
        sx={{
          mt: 0.2,
          fontSize: 18,
          color: theme.customColors.icons.secondary,
          flexShrink: 0,
        }}
      />
    </Tooltip>
  );
};

// Resources card on the Overview tab: the first few resources per category,
// a lock on the ones that need access, and links into the Resources tab.
const ResourcesSummary = ({
  categories,
  resources,
  access,
  onOpen,
  onViewAll,
}) => {
  const theme = useTheme();
  const groups = groupByCategory(
    categories || [],
    (resources || []).filter(isListed)
  );
  if (groups.length === 0) return null;

  return (
    <Card sx={styles.mainCard}>
      <CardHeader sx={styles.cardHeader} title="Resources" />
      {/* Newspaper columns, not a grid: each category sits right under the
          one before it, so a short category never leaves a hole beside a
          long one. */}
      <Box
        sx={{
          px: 2.5,
          pt: 2.5,
          pb: 0.5,
          // The card takes 3/5 of the row on the Overview: two columns
          // keep full document names readable.
          columnCount: { xs: 1, sm: 2 },
          columnGap: "32px",
        }}
      >
        {groups.map((group) => {
          const extra = group.resources.length - OVERVIEW_PER_CATEGORY;
          return (
            <Box
              key={group.id}
              component="section"
              aria-label={`${group.name} summary`}
              sx={{
                breakInside: "avoid",
                pb: 2.5,
                // Keep long unbroken names inside this column (CSS columns
                // otherwise paint them over the next one).
                minWidth: 0,
                maxWidth: "100%",
                overflow: "hidden",
              }}
            >
              <Typography sx={{ fontWeight: 500, mb: 1.25 }}>
                {group.name}
              </Typography>
              <Stack spacing={1} sx={{ minWidth: 0 }}>
                {group.resources
                  .slice(0, OVERVIEW_PER_CATEGORY)
                  .map((resource) => (
                    <Stack
                      key={resource.id}
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                      sx={{ minWidth: 0 }}
                    >
                      <TypeIcon resource={resource} />
                      <Link
                        component="button"
                        variant="body2"
                        textAlign="left"
                        underline="hover"
                        onClick={() => onOpen(resource)}
                        sx={{
                          color: theme.customColors.text.strong,
                          fontWeight: 500,
                          minWidth: 0,
                          flex: 1,
                          overflow: "hidden",
                          display: "block",
                        }}
                      >
                        <TruncatedText value={resource.name} />
                      </Link>
                      {!isUnlocked(resource, access) && (
                        <Tooltip title="Request access" arrow>
                          <LockOutlinedIcon
                            aria-label="Request access"
                            sx={{
                              fontSize: 18,
                              color: theme.customColors.icons.secondary,
                              flexShrink: 0,
                            }}
                          />
                        </Tooltip>
                      )}
                    </Stack>
                  ))}
                {extra > 0 && (
                  <Link
                    component="button"
                    variant="body2"
                    textAlign="left"
                    underline="hover"
                    onClick={() => onViewAll(group.id)}
                    sx={{ color: theme.customColors.text.secondary }}
                  >
                    View {extra} more
                  </Link>
                )}
              </Stack>
            </Box>
          );
        })}
      </Box>
    </Card>
  );
};

export default ResourcesSummary;
